import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import supabaseService from '../services/supabaseService';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled(motion.div)`
  width: 720px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(17,18,23,0.7)' : 'rgba(255,255,255,0.7)'};
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  border-radius: 16px;
  box-shadow: ${p => p.theme.shadow.md};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${p => p.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${p => p.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
`;

const TableWrap = styled.div`
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  position: sticky;
  top: 0;
  background: ${p => p.theme.mode === 'dark' ? '#0f172a' : '#f8fafc'};
  border-bottom: 1px solid ${p => p.theme.mode === 'dark' ? '#333' : '#e5e7eb'};
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid ${p => p.theme.mode === 'dark' ? '#222' : '#f1f5f9'};
  color: ${p => p.theme.mode === 'dark' ? '#e5e7eb' : '#111827'};
`;

export default function AdminModal({ isOpen, onClose }) {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setError('');
      try {
        if (supabaseService.isConfigured()) {
          const rows = await supabaseService.listUsers(200);
          setUsers(Array.isArray(rows) ? rows : []);
        } else {
          // Fallback: 로컬 단일 계정 표기
          const local = JSON.parse(localStorage.getItem('ai_agent_account') || '{}');
          const points = parseInt(localStorage.getItem('ai_agent_points_v1') || '0', 10);
          if (local && (local.email || local.name)) {
            setUsers([{ email: local.email || '-', name: local.name || '-', phone: local.phone || '-', points, created_at: '-' }]);
          } else {
            setUsers([]);
          }
        }
      } catch (e) {
        setError(e.message);
      }
    };
    if (isOpen) load();
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <AnimatePresence>
      <Overlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
        <Modal initial={{ scale: .95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: .95, opacity: 0 }} onClick={e => e.stopPropagation()}>
          <Header>
            <Title>관리자 · 사용자 목록</Title>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none' }}>
              <X size={16} />
            </button>
          </Header>
          {error && <div style={{ color: '#ef4444', padding: '10px 16px', fontSize: 12 }}>오류: {error}</div>}
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>이메일</Th>
                  <Th>이름</Th>
                  <Th>전화번호</Th>
                  <Th>포인트</Th>
                  <Th>가입일</Th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><Td colSpan={5} style={{ color: '#6b7280' }}>표시할 사용자가 없습니다.</Td></tr>
                )}
                {users.map((u, i) => (
                  <tr key={i}>
                    <Td>{u.email || '-'}</Td>
                    <Td>{u.name || '-'}</Td>
                    <Td>{u.phone || '-'}</Td>
                    <Td>{typeof u.points === 'number' ? u.points.toLocaleString() : '-'}</Td>
                    <Td>{u.created_at ? new Date(u.created_at).toLocaleString() : '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Modal>
      </Overlay>
    </AnimatePresence>
  );
}


